import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useParams, Link } from "react-router-dom";
import type { DashboardCommand } from "@pfh/shared";
import { DashboardSection } from "@/components/dashboard-section";
import {
  DataTableShell,
  DataTableHeader,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FirmAvatar } from "@/components/firm-logo";
import { activatePair, getPair, setPairModeOverride, type PairListItem } from "@/lib/api/pairs";
import { PairOverrideToggle } from "@/components/pair-override-toggle";
import { PairSuggestionBanner } from "@/components/pair-suggestion-banner";
import { updatePairLots } from "@/lib/api/account-pairs";
import { extractApiErrorMessage } from "@/lib/api";
import { listDecisions, type DecisionRow } from "@/lib/api/decisions";
import { listTrades, type TradeRow } from "@/lib/api/trades";
import { DecisionDetailDialog } from "@/components/decision-detail-dialog";
import { ManualHedgeOpenDialog } from "@/components/manual-hedge-open-dialog";
import { ChevronLeft, Loader2, Brain } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";
import { useDashboardStore, type AccountLive } from "@/store/dashboard.store";
import { AsleepSnapshotBadge } from "@/components/asleep-snapshot-badge";
import { fmtMoney, fmtPct } from "@/lib/format";
import { LiveCell, LiveDot } from "@/components/live-cell";
import { PairSleepWakeToggle, type PairSleepWakeState } from "@/components/pair-sleep-wake-toggle";
import { AccountEquityChart } from "@/components/account-equity-chart";
import { toast } from "sonner";

const actionColors: Record<string, string> = {
  HOLD: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  PAUSE: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  EXTRACT: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  PIVOT: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  CLOSE: "bg-destructive/10 text-destructive border-destructive/20",
  EXECUTE: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  BLOCK: "bg-destructive/10 text-destructive border-destructive/20",
  CONTINUE: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  ESCALATE: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  ALERT: "bg-destructive/10 text-destructive border-destructive/20",
};

function parentModeLabel(mode: PairListItem["mode"]): string {
  if (mode === "manual") return "Manual";
  if (mode === "semi_auto") return "Semi";
  return "Auto";
}

function challengePhaseLabel(
  longPhase: PairListItem["longAccount"]["phase"],
  shortPhase: PairListItem["shortAccount"]["phase"],
): string {
  if (longPhase === shortPhase) {
    if (longPhase === "step1") return "Step 1";
    if (longPhase === "step2") return "Step 2";
    return "Funded";
  }
  return "Mixed Phase";
}

export function PairDetailPage({
  idOverride,
}: {
  /** Optional override — when this component is mounted from the nested
   *  `/pairs/:slug/:symbol` route the caller resolves the symbol pair's
   *  slug externally and passes it in. In the legacy `/pairs/:id` route
   *  the param itself is the id. */
  idOverride?: string;
} = {}): ReactNode {
  const { id: paramId } = useParams<{ id: string }>();
  const id = idOverride ?? paramId;
  const [pair, setPair] = useState<PairListItem | null | undefined>(undefined);
  const [decisions, setDecisions] = useState<DecisionRow[] | null>(null);
  const [trades, setTrades] = useState<TradeRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedDecisionId, setSelectedDecisionId] = useState<number | null>(null);

  const loadPair = useCallback((): void => {
    if (!id) return;
    setLoadError(null);
    setPair(undefined);
    setDecisions(null);
    Promise.all([getPair(id), listDecisions(id)])
      .then(([p, d]) => {
        setPair(p);
        setDecisions(d);
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : String(e));
        setPair(null);
        setDecisions([]);
      });
  }, [id]);

  useEffect(() => {
    loadPair();
  }, [loadPair]);

  // Trade history polls on a 10s loop. The close event also arrives
  // on the WS (position:update event='close'); polling covers the
  // backfill window + is cheap (one D1 read, <100 rows).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const fetchTrades = () => {
      listTrades(id)
        .then((rows) => {
          if (!cancelled) setTrades(rows);
        })
        .catch(() => {
          if (!cancelled && trades === null) setTrades([]);
        });
    };
    fetchTrades();
    const iv = setInterval(fetchTrades, 10_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Subscribe to the HedgePair DO's WS. Broadcasts position:update +
  // account:tick are ingested into the store so we can render live
  // equity / open PnL per side without re-fetching REST.
  const { send: sendCommand } = useHedgeSocket(pair?.id ?? null);
  const liveAccounts = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.accounts ?? null) : null,
  );
  const livePositions = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.positions ?? null) : null,
  );
  const connectionStatus = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.connectionStatus ?? "closed") : "closed",
  );
  const liveSleep = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.sleep ?? null) : null,
  );
  const pairSleepState: PairSleepWakeState | null = liveSleep
    ? {
        mode: liveSleep.mode,
        manualState: liveSleep.manualState,
        currentState: liveSleep.currentState,
      }
    : pair?.pairSleep
      ? {
          mode: pair.pairSleep.mode,
          manualState: pair.pairSleep.manualState,
          currentState: pair.pairSleep.currentState,
        }
      : null;

  if (pair === undefined) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading pair">
        <Header id={id} />
        <DashboardSection>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-3 w-64" />
            <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col gap-1.5 rounded-lg border border-border/40 p-3">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>
        <DashboardSection>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full rounded-md" />
            ))}
          </div>
        </DashboardSection>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-6">
        <Header id={id} />
        <DashboardSection>
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <p role="alert" aria-live="polite" className="text-sm text-destructive">
              Couldn’t load pair: {loadError}
            </p>
            <Button size="sm" variant="outline" onClick={loadPair}>
              Retry
            </Button>
          </div>
        </DashboardSection>
      </div>
    );
  }

  if (pair === null) {
    return (
      <div className="flex flex-col gap-6">
        <Header id={id} />
        <DashboardSection>
          <p className="py-8 text-center text-sm text-muted-foreground">
            Pair {id} not found.
          </p>
        </DashboardSection>
      </div>
    );
  }

  const phaseLabel = challengePhaseLabel(pair.longAccount.phase, pair.shortAccount.phase);
  const sendPairCommand = (cmd: DashboardCommand): boolean => sendCommand(cmd);

  return (
    <div className="flex flex-col gap-6 font-sans">
      {/* Back affordance lives in the TopBar breadcrumb now — no inline
          back button here. Header is just the symbol + metadata. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-medium text-foreground sm:text-3xl">{pair.symbol}</h1>
            <LiveDot status={connectionStatus} />
          </div>
          <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
            <span className="truncate font-mono">{pair.id}</span>
            <span aria-hidden>·</span>
            <TargetLotsInline pair={pair} onSaved={(lots) => setPair({ ...pair, lots })} />
            <span aria-hidden>·</span>
            <span>{phaseLabel}</span>
            <span aria-hidden>·</span>
            <span
              className={cn(
                "capitalize",
                pair.status === "active" && "text-emerald-500",
                pair.status === "setup" && "text-sky-500",
                pair.status === "closed" && "text-muted-foreground",
              )}
            >
              {pair.status}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pair.status === "active" && (
            <div className="flex flex-col items-end gap-1">
              <PairOverrideToggle
                override={pair.modeOverride}
                parentMode={pair.parentAiMode ?? "manual"}
                status={pair.status}
                size="sm"
                onChange={async (next) => {
                  try {
                    await setPairModeOverride(pair.slug, next);
                    const fresh = await getPair(pair.slug);
                    if (fresh) setPair(fresh);
                  } catch (e) {
                    toast.error(extractApiErrorMessage(e));
                  }
                }}
              />
              {pair.modeOverride == null && pair.parentAiMode != null && (
                <span className="text-[10px] text-muted-foreground">
                  Inherits parent: {parentModeLabel(pair.parentAiMode)}
                </span>
              )}
            </div>
          )}
          {pair.status === "setup" && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await activatePair(pair.slug, "manual");
                  const fresh = await getPair(pair.slug);
                  if (fresh) setPair(fresh);
                  toast.success("Pair activated in manual mode");
                } catch (e) {
                  toast.error(extractApiErrorMessage(e));
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent/90"
            >
              Activate Pair
            </button>
          )}
          {pair.accountPairSlug && pairSleepState && (
            <>
              {/* Divider — separates the AI override picker (left)
                  from the device-state Sleep/Wake toggle (right). They
                  govern different layers (Haiku vs. Brave); grouping
                  them visually was confusing operators. */}
              {pair.status === "active" && (
                <span aria-hidden className="mx-0.5 h-5 w-px bg-border/60" />
              )}
              <PairSleepWakeToggle
                accountPairSlug={pair.accountPairSlug}
                state={pairSleepState}
                size="sm"
              />
            </>
          )}
        </div>
      </div>

      {/* Setup-state hint + semi_auto suggestion banner. */}
      {pair.status === "setup" && (
        <DashboardSection>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="text-sm font-medium text-foreground">
              This pair is in <span className="text-accent">setup</span>.
            </p>
            <p className="max-w-md text-xs text-muted-foreground">
              No AI heartbeat fires and no orders can be placed until you
              complete setup. Starts in manual mode — switch to Semi or
              Auto later from the toggle.
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  await activatePair(pair.slug, "manual");
                  const fresh = await getPair(pair.slug);
                  if (fresh) setPair(fresh);
                  toast.success("Setup complete — pair is now active");
                } catch (e) {
                  toast.error(extractApiErrorMessage(e));
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-accent/90"
            >
              Complete Setup
            </button>
          </div>
        </DashboardSection>
      )}
      {pair.status === "active" && pair.mode === "semi_auto" && (
        <PairSuggestionBanner pairSlug={pair.slug} />
      )}

      {/* Pair account cards */}
      <div className="grid gap-3 md:grid-cols-2">
        {[pair.longAccount, pair.shortAccount].map((side) => {
          const live: AccountLive | undefined = liveAccounts?.[side.accountId];
          const equity = live?.equity ?? side.equity;
          // Per-leg open P&L for THIS symbol pair only — sum positions
          // in this pair's slice that belong to the leg's account. Do
          // NOT use live.accountAggregateOpenPnl: that's the broker-
          // footer account-wide total, which would bleed P&L from
          // other symbol pairs the same account is hedging on.
          // `livePositions` is already subscribed at the top of this
          // component, so this re-renders correctly on every tick.
          let pnl: number | null = null;
          if (livePositions) {
            pnl = 0;
            for (const p of Object.values(livePositions)) {
              if (p.accountId === side.accountId) pnl += p.openPnl;
            }
          }
          const balance = live?.balance ?? null;
          const isOnline = live?.online ?? side.online;
          const isLive = live != null;
          return (
            <DashboardSection key={side.side}>
              <div className="flex items-center gap-3">
                <FirmAvatar
                  src={side.firmLogo}
                  name={side.firmName}
                  size="default"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {side.firmName}
                    </p>
                    <span
                      className={cn(
                        "ml-auto h-2 w-2 rounded-full",
                        isOnline ? "bg-emerald-500" : "bg-muted-foreground/30",
                      )}
                      title={isOnline ? "Online" : "Offline"}
                    />
                  </div>
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {side.platformType} · {side.adapter} · {side.accountId}
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <LiveCell label="Equity" value={fmtMoney(equity)} live={isLive} />
                <LiveCell
                  label="P&L"
                  value={pnl == null ? "—" : fmtMoney(pnl)}
                  color={
                    pnl == null
                      ? undefined
                      : pnl >= 0
                        ? "text-emerald-500"
                        : "text-destructive"
                  }
                  live={isLive}
                />
                <LiveCell
                  label={balance != null ? "Balance" : "Daily DD"}
                  value={
                    balance != null
                      ? fmtMoney(balance)
                      : fmtPct(side.dailyDdUsed, side.dailyDdLimit)
                  }
                />
                <LiveCell
                  label="Total DD"
                  value={fmtPct(side.totalDdUsed, side.totalDdLimit)}
                />
              </div>
              <AccountEquityChart
                className="mt-3"
                side={side.side}
                startBalance={balance}
                liveEquity={live?.equity ?? null}
                isLive={isLive}
                dailyDdLimit={side.dailyDdLimit}
                dailyDdUsed={side.dailyDdUsed}
                totalDdLimit={side.totalDdLimit}
                totalDdUsed={side.totalDdUsed}
              />
              {live?.updatedAt && (
                <p className="mt-2 font-mono text-[10px] text-muted-foreground/80">
                  Updated {new Date(live.updatedAt).toLocaleTimeString()}
                </p>
              )}
              {(() => {
                const legState = liveSleep?.legStates?.[side.accountId];
                const legAsleep =
                  legState === "asleep" ||
                  legState === "orphan" ||
                  // Until a live legState event lands, fall back to the
                  // pair-level sleep snapshot embedded in the REST
                  // response so the badge appears on initial paint.
                  (legState == null && pairSleepState?.currentState === "asleep");
                return (
                  <AsleepSnapshotBadge
                    isAsleep={!!legAsleep}
                    snapshot={side.lastSleepSnapshot ?? null}
                    snapshotAt={side.lastSleepSnapshotAt ?? null}
                    className="mt-2"
                  />
                );
              })()}
            </DashboardSection>
          );
        })}
      </div>

      {/* Positions — live state + Open / Modify / Close in one section */}
      <PositionsSection
        pair={pair}
        livePositions={livePositions}
        canSend={connectionStatus === "open"}
        onCommand={sendPairCommand}
      />

      {/* Decision history for this pair */}
      <DashboardSection
        title="Decision History"
        className="overflow-hidden"
      >
        {decisions === null ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-label="Loading decisions" className="h-4 w-4 animate-spin text-accent" />
          </div>
        ) : decisions.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Brain aria-hidden="true" className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Bot hasn’t traded this pair yet — no decisions logged.
            </p>
          </div>
        ) : (
          <>
            <DataTableShell minWidth={760}>
              <DataTableHeader
                labels={["Time", "Tier", "Action", "Compliance", "Conf.", "Reason"]}
              />
              <tbody>
                {decisions.map((d, i) => {
                  const action = d.claude_action.toUpperCase();
                  return (
                    <DataTableRow
                      key={d.id}
                      index={i}
                      className="cursor-pointer"
                      onClick={() => setSelectedDecisionId(Number(d.id))}
                    >
                      <DataTableCell align="left" className="px-4 font-mono text-[11px] font-light text-muted-foreground">
                        {new Date(d.created_at).toISOString().slice(11, 19)}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-[11px] text-muted-foreground">
                        {d.tier}
                      </DataTableCell>
                      <DataTableCell>
                        <Badge variant="outline" className={cn("text-[10px]", actionColors[action])}>
                          {action}
                        </Badge>
                      </DataTableCell>
                      <DataTableCell className="text-[11px] text-muted-foreground">
                        {d.compliance_status ?? "—"}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-xs">
                        {d.confidence === null
                          ? "—"
                          : `${Math.round(d.confidence * 100)}%`}
                      </DataTableCell>
                      <DataTableCell className="max-w-[400px] truncate text-[11px] text-muted-foreground">
                        {d.reason}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTableShell>

            {/* Mobile cards */}
            <ul className="flex flex-col gap-2 lg:hidden">
              {decisions.map((d) => {
                const action = d.claude_action.toUpperCase();
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedDecisionId(Number(d.id))}
                      className="flex w-full flex-col gap-2 rounded-xl border border-border/40 bg-card p-3 text-left transition-colors hover:border-foreground/20"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge variant="outline" className={cn("text-[10px]", actionColors[action])}>
                          {action}
                        </Badge>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {new Date(d.created_at).toISOString().slice(11, 19)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span className="font-mono">{d.tier}</span>
                        <span>· {d.compliance_status ?? "—"}</span>
                        {d.confidence !== null && (
                          <span className="ml-auto font-mono text-xs text-foreground">
                            {Math.round(d.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      <p className="line-clamp-3 text-[11px] text-muted-foreground">{d.reason}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </DashboardSection>

      <DashboardSection title="Trade History" className="overflow-hidden">
        {trades === null ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 aria-label="Loading trades" className="h-4 w-4 animate-spin text-accent" />
          </div>
        ) : trades.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Brain aria-hidden="true" className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No trades on this pair yet — nothing’s been opened.
            </p>
          </div>
        ) : (
          <>
            <DataTableShell minWidth={860}>
              <DataTableHeader
                labels={["Time", "Ticket", "Account", "Side", "Lots", "Entry", "Close", "P&L"]}
              />
              <tbody>
                {trades.map((t, i) => {
                  const isOpen = t.closed_at === null;
                  const when = t.closed_at ?? t.opened_at;
                  return (
                    <DataTableRow key={t.id} index={i}>
                      <DataTableCell align="left" className="px-4 font-mono text-[11px] font-light text-muted-foreground">
                        {new Date(when).toISOString().slice(5, 16).replace("T", " ")}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-[11px]">
                        {t.ticket}
                      </DataTableCell>
                      <DataTableCell className="text-[11px] text-muted-foreground">
                        {t.firm_name ?? t.account_id}
                        {t.login && <span className="text-muted-foreground/70"> · {t.login}</span>}
                      </DataTableCell>
                      <DataTableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px]",
                            t.side === "buy"
                              ? "border-emerald-500/30 text-emerald-500"
                              : "border-rose-500/30 text-rose-500",
                          )}
                        >
                          {t.side.toUpperCase()}
                        </Badge>
                      </DataTableCell>
                      <DataTableCell className="font-mono text-xs">
                        {t.lots}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-[11px] text-muted-foreground">
                        {t.entry_price.toFixed(2)}
                      </DataTableCell>
                      <DataTableCell className="font-mono text-[11px] text-muted-foreground">
                        {t.close_price != null ? t.close_price.toFixed(2) : "—"}
                      </DataTableCell>
                      <DataTableCell>
                        {isOpen ? (
                          <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">
                            Open
                          </Badge>
                        ) : t.pnl == null ? (
                          <span className="font-mono text-xs text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={cn(
                              "font-mono text-xs font-semibold tabular-nums",
                              t.pnl >= 0 ? "text-emerald-500" : "text-destructive",
                            )}
                          >
                            {fmtMoney(t.pnl)}
                          </span>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTableShell>

            {/* Mobile cards */}
            <ul className="flex flex-col gap-2 lg:hidden">
              {trades.map((t) => {
                const isOpen = t.closed_at === null;
                const when = t.closed_at ?? t.opened_at;
                return (
                  <li
                    key={t.id}
                    className="flex flex-col gap-2 rounded-xl border border-border/40 bg-card p-3 text-xs"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px]",
                            t.side === "buy"
                              ? "border-emerald-500/30 text-emerald-500"
                              : "border-rose-500/30 text-rose-500",
                          )}
                        >
                          {t.side.toUpperCase()}
                        </Badge>
                        <span className="font-mono text-[11px] text-muted-foreground">#{t.ticket}</span>
                      </div>
                      {isOpen ? (
                        <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">
                          Open
                        </Badge>
                      ) : t.pnl == null ? (
                        <span className="font-mono text-xs text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={cn(
                            "font-mono text-xs font-semibold tabular-nums",
                            t.pnl >= 0 ? "text-emerald-500" : "text-destructive",
                          )}
                        >
                          {fmtMoney(t.pnl)}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {t.firm_name ?? t.account_id}
                      {t.login && <span className="text-muted-foreground/70"> · {t.login}</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Lots</span>
                        <span className="font-mono text-foreground">{t.lots}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
                        <span className="font-mono text-foreground">
                          {new Date(when).toISOString().slice(5, 16).replace("T", " ")}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Entry</span>
                        <span className="font-mono text-foreground">{t.entry_price.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Close</span>
                        <span className="font-mono text-foreground">
                          {t.close_price != null ? t.close_price.toFixed(2) : "—"}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </DashboardSection>

      <DecisionDetailDialog
        decisionId={selectedDecisionId}
        open={selectedDecisionId != null}
        onOpenChange={(o) => { if (!o) setSelectedDecisionId(null); }}
      />
    </div>
  );
}

function Header({ id }: { id: string | undefined }): ReactNode {
  return (
    <div className="flex items-center gap-3">
      <Link
        to="/"
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
      </Link>
      <h1 className="text-2xl font-light text-foreground">Pair {id ?? ""}</h1>
    </div>
  );
}

/**
 * Inline-editable target lots. Actual trade size is still clamped at
 * stage time to min(target, long.sustainable, short.sustainable) — this
 * is the operator's desired UPPER bound. Edit stays disabled when the
 * parent account_pair slug isn't available (legacy rows).
 */
function TargetLotsInline({
  pair,
  onSaved,
}: {
  pair: PairListItem;
  onSaved: (lots: number) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(pair.lots));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!pair.accountPairSlug) {
    return <span className="font-mono">{pair.lots} Lots</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="font-mono underline decoration-dotted underline-offset-4 transition-colors hover:text-foreground"
        onClick={() => {
          setValue(String(pair.lots));
          setErr(null);
          setEditing(true);
        }}
      >
        {pair.lots} Lots
      </button>
    );
  }

  const submit = async (): Promise<void> => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
      setErr("Lots must be > 0");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const res = await updatePairLots(pair.accountPairSlug!, pair.slug, n);
      onSaved(res.lots);
      setEditing(false);
    } catch (e) {
      setErr(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={saving}
        className="h-6 w-16 px-1.5 py-0 font-mono text-xs"
        inputMode="decimal"
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit();
          if (e.key === "Escape") setEditing(false);
        }}
        autoFocus
      />
      <span className="text-muted-foreground">Lots</span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-6 px-2 text-xs"
        disabled={saving}
        onClick={() => void submit()}
      >
        {saving ? "Saving…" : "Save"}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-xs"
        disabled={saving}
        onClick={() => setEditing(false)}
      >
        Cancel
      </Button>
      {err && <span className="text-xs text-destructive">{err}</span>}
    </span>
  );
}

/**
 * Positions section — one UI block that owns all live-position state
 * AND every action the operator can take against it: open a fresh
 * hedge, close everything, modify the SL/TP on a single ticket, or
 * close a single ticket. Replaces the previous two-section layout
 * (separate "Manual Controls" above + "Open Positions" table below),
 * which was hard to read because the commands and the data they
 * act on were visually split.
 *
 * Layout:
 *   - Header: title + counts + "Open Hedge" and "Close All" buttons.
 *   - Body: one card per live ticket, with a prominent Open P&L, the
 *     entry/SL/TP/lots in a stat grid, and Modify + Close buttons.
 *   - Empty state: clear call-to-action using the same Open Hedge CTA.
 */
function PositionsSection({
  pair,
  livePositions,
  canSend,
  onCommand,
}: {
  pair: PairListItem;
  livePositions: Record<string, import("@/store/dashboard.store").PositionLive> | null;
  canSend: boolean;
  onCommand: (cmd: DashboardCommand) => boolean;
}): ReactNode {
  type PositionLive = import("@/store/dashboard.store").PositionLive;

  const longId = pair.longAccount.accountId;
  const shortId = pair.shortAccount.accountId;

  const rows = Object.values(livePositions ?? {})
    .filter((p) => p.accountId === longId || p.accountId === shortId)
    .sort(
      (a, b) =>
        a.accountId.localeCompare(b.accountId) || a.ticket - b.ticket,
    );

  const firmFor = (accountId: string): string =>
    accountId === longId ? pair.longAccount.firmName : pair.shortAccount.firmName;
  const legFor = (accountId: string): "long" | "short" =>
    accountId === longId ? "long" : "short";

  // ── Modify dialog state (SL/TP on a single ticket) ────────
  const [editing, setEditing] = useState<PositionLive | null>(null);
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");

  const openModify = (p: PositionLive): void => {
    setEditing(p);
    setStopLoss(p.stopLoss == null ? "" : String(p.stopLoss));
    setTakeProfit(p.takeProfit == null ? "" : String(p.takeProfit));
  };

  const submitModify = (): void => {
    if (!editing) return;
    if (!canSend) {
      toast.error("Pair socket is not connected yet.");
      return;
    }
    const parsedSl = parseOptionalNumber(stopLoss);
    const parsedTp = parseOptionalNumber(takeProfit);
    if (parsedSl === undefined || parsedTp === undefined) {
      toast.error("SL and TP must be valid numbers or empty.");
      return;
    }
    const ok = onCommand({
      type: "position:modify",
      payload: {
        account: editing.accountId,
        ticket: editing.ticket,
        stopLoss: parsedSl,
        takeProfit: parsedTp,
      },
    });
    if (!ok) {
      toast.error("Couldn't send the modify command.");
      return;
    }
    toast.success(`Modify sent · ticket #${editing.ticket}`);
    setEditing(null);
  };

  // ── Close single ticket / both legs ───────────────────────
  const closeOne = (p: PositionLive): void => {
    if (!canSend) {
      toast.error("Pair socket is not connected yet.");
      return;
    }
    const ok = onCommand({
      type: "position:close",
      payload: { account: p.accountId, ticket: p.ticket },
    });
    if (!ok) {
      toast.error("Couldn't send the close command.");
      return;
    }
    toast.success(`Close sent · ticket #${p.ticket}`);
  };

  const closeAll = (): void => {
    if (!canSend) {
      toast.error("Pair socket is not connected yet.");
      return;
    }
    // map (not every) — both legs must be attempted regardless of one's result.
    const cmds: DashboardCommand[] = [
      {
        type: "position:close",
        payload: { account: pair.longAccount.accountId, ticket: "all" },
      },
      {
        type: "position:close",
        payload: { account: pair.shortAccount.accountId, ticket: "all" },
      },
    ];
    const ok = cmds.map((c) => onCommand(c)).every(Boolean);
    if (!ok) {
      toast.error("Couldn't send close-all.");
      return;
    }
    toast.success("Close-all sent to both legs.");
  };

  const renderCard = (p: PositionLive): ReactNode => {
    const leg = legFor(p.accountId);
    return (
      <div
        key={`${p.accountId}:${p.ticket}`}
        className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] uppercase tracking-wide",
                leg === "long"
                  ? "border-emerald-500/40 text-emerald-500"
                  : "border-rose-500/40 text-rose-500",
              )}
            >
              {leg} leg
            </Badge>
            <span className="truncate text-sm font-medium text-foreground">
              {firmFor(p.accountId)}
            </span>
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">
            #{p.ticket}
          </span>
        </div>

        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              "font-mono text-2xl font-bold tabular-nums",
              p.openPnl >= 0 ? "text-emerald-500" : "text-destructive",
            )}
          >
            {p.openPnl >= 0 ? "+" : ""}
            {fmtMoney(p.openPnl)}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            open P&amp;L
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 md:grid-cols-6">
          <StatBlock label="Symbol" value={p.symbol} mono />
          <StatBlock
            label="Side"
            value={
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] capitalize",
                  p.side === "buy"
                    ? "border-emerald-500/30 text-emerald-500"
                    : "border-rose-500/30 text-rose-500",
                )}
              >
                {p.side}
              </Badge>
            }
          />
          <StatBlock label="Lots" value={String(p.lots)} mono />
          <StatBlock label="Entry" value={String(p.entry)} mono />
          <StatBlock
            label="SL"
            value={
              p.stopLoss == null ? (
                <span className="text-destructive/70">—</span>
              ) : (
                <span className="text-amber-500">{p.stopLoss}</span>
              )
            }
            mono
          />
          <StatBlock
            label="TP"
            value={
              p.takeProfit == null ? (
                <span className="text-muted-foreground/70">—</span>
              ) : (
                <span className="text-emerald-500/80">{p.takeProfit}</span>
              )
            }
            mono
          />
        </div>

        <div className="mt-1 flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-1"
            disabled={!canSend}
            onClick={() => openModify(p)}
          >
            Modify SL / TP
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/10"
            disabled={!canSend}
            onClick={() => closeOne(p)}
          >
            Close Position
          </Button>
        </div>
      </div>
    );
  };

  const hasPositions = rows.length > 0;

  return (
    <DashboardSection className="overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border/60 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Positions</h3>
          <p className="text-xs text-muted-foreground">
            {hasPositions
              ? `${rows.length} open · live via WebSocket`
              : canSend
                ? "Both legs are flat — open a hedge to start."
                : "Pair socket offline — reconnect to act."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ManualHedgeOpenDialog
            pair={pair}
            livePositions={livePositions}
            renderTrigger={(openDialog) => (
              <Button type="button" size="sm" onClick={openDialog}>
                Open Hedge
              </Button>
            )}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={closeAll}
            disabled={!canSend || !hasPositions}
          >
            Close All
          </Button>
        </div>
      </div>

      {!hasPositions ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            No live positions on this pair.
          </p>
          <p className="text-xs text-muted-foreground/70">
            When the bot opens a hedge (or you click Open Hedge), both
            legs appear here with live P&amp;L + Modify / Close actions.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 pt-3 md:grid-cols-2">
          {rows.map(renderCard)}
        </div>
      )}

      <Dialog
        open={editing != null}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modify SL / TP</DialogTitle>
            <DialogDescription>
              {editing
                ? `Update the bracket on ticket #${editing.ticket} (${firmFor(editing.accountId)}). Empty field removes that level.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <label className="text-xs font-medium text-muted-foreground">
                Stop Loss
              </label>
              <Input
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder="Empty = no SL"
                inputMode="decimal"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-xs font-medium text-muted-foreground">
                Take Profit
              </label>
              <Input
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder="Empty = no TP"
                inputMode="decimal"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={submitModify} disabled={!canSend}>
              Send Modify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardSection>
  );
}

function StatBlock({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "text-xs font-semibold text-foreground tabular-nums",
          mono && "font-mono",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseOptionalNumber(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}
